import { nanoid } from "nanoid";
/** Rapid renames and column drags coalesce before reaching the worker, as the hooks they replace did. */
const RENAME_DEBOUNCE_MS = 300;
const COLUMN_UPDATE_DEBOUNCE_MS = 750;
const toError = (e) => ({ code: "network", message: e instanceof Error ? e.message : String(e) });
export const createPagesApi = (store, send) => {
    const run = async (token, local, command) => {
        store.begin(token, local);
        let result;
        try {
            result = await send(command);
        }
        catch (e) {
            store.rollback(token);
            throw toError(e);
        }
        if (!result.ok) {
            store.rollback(token);
            throw result.error;
        }
        store.commit(token);
    };
    const coalescer = (ms, build) => {
        const batches = new Map();
        return (id, patch) => new Promise((resolve, reject) => {
            const token = nanoid();
            store.begin(token, build(id, patch).local);
            const existing = batches.get(id);
            if (existing) {
                clearTimeout(existing.timer);
                existing.tokens.push(token);
                existing.patch = { ...existing.patch, ...patch };
                existing.waiters.push({ resolve, reject });
            }
            const batch = existing ?? {
                tokens: [token],
                patch,
                waiters: [{ resolve, reject }],
                timer: 0,
            };
            batch.timer = setTimeout(async () => {
                batches.delete(id);
                const settle = (fn) => batch.waiters.forEach(fn);
                let result;
                try {
                    result = await send(build(id, batch.patch).command);
                }
                catch (e) {
                    batch.tokens.forEach(store.rollback);
                    return settle((w) => w.reject(toError(e)));
                }
                if (!result.ok) {
                    batch.tokens.forEach(store.rollback);
                    return settle((w) => w.reject(result.error));
                }
                batch.tokens.forEach(store.commit);
                settle((w) => w.resolve());
            }, ms);
            batches.set(id, batch);
        });
    };
    const rename = coalescer(RENAME_DEBOUNCE_MS, (id, patch) => ({
        local: { pages: { upserts: [{ id, ...patch }] } },
        command: { kind: "rename", id, name: patch.name },
    }));
    const updateColumn = coalescer(COLUMN_UPDATE_DEBOUNCE_MS, (id, patch) => ({
        local: { columns: { upserts: [{ id, ...patch }] } },
        command: { kind: "updateColumn", id, patch },
    }));
    const stored = (id) => {
        const page = store.getPage(id);
        if (!page)
            throw { code: "server", message: `page ${id} vanished` };
        return page;
    };
    return {
        subscribe: store.subscribe,
        create: async ({ name, type, idx }) => {
            const id = nanoid();
            const row = { id, type, name, order: idx, ownerId: "", members: [] };
            await run(nanoid(), { pages: { upserts: [row] } }, { kind: "create", id, name, type, idx });
            return stored(id);
        },
        rename: (id, name) => rename(id, { name }),
        move: (id, order) => run(nanoid(), { pages: { upserts: [{ id, order }] } }, { kind: "move", id, order }),
        duplicate: async (id, { name, idx, withFormatting = true }) => {
            const newId = nanoid();
            const source = store.getPage(id);
            const row = { id: newId, type: source?.type ?? "market", name, order: idx, ownerId: "", members: [] };
            await run(nanoid(), { pages: { upserts: [row] } }, { kind: "duplicate", id, newId, name, idx, withFormatting });
            return stored(newId);
        },
        remove: (id) => run(nanoid(), { pages: { removes: [id] } }, { kind: "remove", id }),
        removeMany: (ids) => run(nanoid(), { pages: { removes: [...ids] } }, { kind: "removeMany", ids }),
        share: (id, userIds) => {
            const members = [...new Set([...(store.getPage(id)?.members ?? []), ...userIds])];
            return run(nanoid(), { pages: { upserts: [{ id, members }] } }, { kind: "share", id, userIds });
        },
        columns: {
            subscribe: store.subscribeColumns,
            insert: (columns) => run(nanoid(), { columns: { upserts: [...columns] } }, { kind: "insertColumns", columns }),
            update: updateColumn,
            remove: (id) => run(nanoid(), { columns: { removes: [id] } }, { kind: "removeColumn", id }),
        },
    };
};
//# sourceMappingURL=pagesApi.js.map